'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useCookieConsentStore, type CookiePreferences } from '@shared/store/cookie-consent.store';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { SecondaryButton } from '@shared/ui/button/SecondaryButton';
import { TextButton } from '@shared/ui/button/TextButton';
import { Typography } from '@shared/ui/typography/Typography';
import { Toggle } from '@shared/ui/Toggle';
import { cn } from '@/src/shared/lib/utils';

/**
 * Cookie Consent Banner - PIPEDA Compliant
 *
 * Shows on first visit and allows users to:
 * - Accept all cookies
 * - Reject optional cookies
 * - Customize preferences
 *
 * Preferences are saved to localStorage and synced to backend (if authenticated)
 */
export function CookieConsentBanner() {
  const t = useTranslations('cookieConsent');
  const {
    preferences,
    bannerVisible,
    customizeMode,
    acceptAll,
    rejectOptional,
    savePreferences,
    toggleCustomize,
    loadPreferences,
  } = useCookieConsentStore();

  const [customPrefs, setCustomPrefs] = useState<CookiePreferences>({
    essential: true,
    functional: true,
    analytics: false,
    marketing: false,
  });

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    loadPreferences();
  }, [loadPreferences]);

  // Prevent hydration mismatch
  if (!mounted || !bannerVisible) return null;

  const handleSaveCustom = () => {
    savePreferences(customPrefs);
  };

  // Simple Mode - Default view
  if (!customizeMode) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-border-primary shadow-lg animate-slide-up">
        <div className="container max-w-6xl mx-auto p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Content */}
            <div className="flex-1">
              <Typography variant="bodyMd" weight="semibold" className="mb-1">
                {t('title')}
              </Typography>
              <Typography variant="bodySm" color="secondary">
                {t('description')}{' '}
                <Link href="/legal/cookies" className="text-accent-1 hover:underline">
                  {t('learnMore')}
                </Link>
              </Typography>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-2">
              <PrimaryButton onClick={acceptAll} size="sm">
                {t('acceptAll')}
              </PrimaryButton>
              <SecondaryButton onClick={rejectOptional} size="sm">
                {t('rejectOptional')}
              </SecondaryButton>
              <TextButton onClick={toggleCustomize} size="sm">
                {t('customize')}
              </TextButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Customize Mode - Detailed preferences
  return (
    <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <Typography variant="h3" className="mb-4">
            {t('customizeTitle')}
          </Typography>

          {/* Cookie Categories */}
          <div className="space-y-4 mb-6">
            {/* Essential Cookies */}
            <div className="border border-border-primary rounded-lg p-4 bg-surface-secondary">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Typography variant="bodyMd" weight="semibold">
                    {t('essential')}
                  </Typography>
                  <Typography variant="bodySm" color="secondary">
                    {t('essentialDesc')}
                  </Typography>
                </div>
                <Toggle checked={true} disabled />
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="border border-border-primary rounded-lg p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Typography variant="bodyMd" weight="semibold">
                    {t('functional')}
                  </Typography>
                  <Typography variant="bodySm" color="secondary">
                    {t('functionalDesc')}
                  </Typography>
                </div>
                <Toggle
                  checked={customPrefs.functional}
                  onChange={(val) => setCustomPrefs({ ...customPrefs, functional: val })}
                />
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="border border-border-primary rounded-lg p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Typography variant="bodyMd" weight="semibold">
                    {t('analytics')}
                  </Typography>
                  <Typography variant="bodySm" color="secondary">
                    {t('analyticsDesc')}
                  </Typography>
                </div>
                <Toggle
                  checked={customPrefs.analytics}
                  onChange={(val) => setCustomPrefs({ ...customPrefs, analytics: val })}
                />
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="border border-border-primary rounded-lg p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Typography variant="bodyMd" weight="semibold">
                    {t('marketing')}
                  </Typography>
                  <Typography variant="bodySm" color="secondary">
                    {t('marketingDesc')}
                  </Typography>
                </div>
                <Toggle
                  checked={customPrefs.marketing}
                  onChange={(val) => setCustomPrefs({ ...customPrefs, marketing: val })}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <PrimaryButton onClick={handleSaveCustom} className="flex-1">
              {t('savePreferences')}
            </PrimaryButton>
            <SecondaryButton onClick={toggleCustomize}>
              {t('back')}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
